
import { useState, useCallback } from 'react';
import { PendingImageUpload } from '@/types/chat';
import { chatImageService } from '@/services/chatImageService';
import { useToast } from '@/hooks/use-toast';

export const useImageUpload = () => {
  const [pendingImages, setPendingImages] = useState<PendingImageUpload[]>([]);
  const { toast } = useToast();

  const addImages = useCallback((files: File[]) => {
    const validation = chatImageService.validateFiles(files);
    
    if (!validation.valid) {
      validation.errors.forEach(error => {
        toast({
          title: "Invalid file",
          description: error,
          variant: "destructive"
        });
      });
      return;
    }

    // Create pending images with preview URLs (no upload)
    const newImages: PendingImageUpload[] = files.map(file => ({
      file,
      preview: chatImageService.createPreviewUrl(file),
      uploading: false,
      uploaded: false,
      progress: 0
    }));

    // Check total count
    setPendingImages(prev => {
      const combined = [...prev, ...newImages];
      if (combined.length > 3) {
        toast({
          title: "Too many images",
          description: "Maximum 3 images allowed per message",
          variant: "destructive"
        });
        // Clean up new previews
        newImages.forEach(img => chatImageService.revokePreviewUrl(img.preview));
        return prev;
      }
      return combined;
    });
  }, [toast]);

  // Remove the uploadNewImages function as we don't auto-upload anymore

  const removeImage = useCallback((index: number) => {
    setPendingImages(prev => {
      const image = prev[index];
      if (image && image.preview.startsWith('blob:')) {
        chatImageService.revokePreviewUrl(image.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const clearImages = useCallback(() => {
    setPendingImages(prev => {
      prev.forEach(image => {
        if (image.preview.startsWith('blob:')) {
          chatImageService.revokePreviewUrl(image.preview);
        }
      });
      return [];
    });
  }, []);

  // Since we don't upload until send, images are always "ready" if they exist
  const canSend = true; // No upload state to check
  const hasImages = pendingImages.length > 0;

  return {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    canSend,
    hasImages
  };
};
